import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsCart, BsTrash, BsArrowUpRight } from "react-icons/bs";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { ApiPaths } from "../../../api/paths";
import CustomButton from "../../../components/custom-button/CustomButton";
import DataTable from "../../../components/data-table/DataTable";
import AssetsShieldIcon from "../../../components/icons/AssetsShieldIcon";
import Pagination from "../../../components/pagination/Pagination";
import useData from "../../../hooks/use-data";
import useDisclosure from "../../../hooks/use-disclosure";
import usePagination from "../../../hooks/use-pagination";
import { ProductType } from "../../../types/product";
import { CartType } from "../../../types/cart";
import { PaginatedApiResponseType } from "../../../types/common";
import { ActionType, ColumnType } from "../../../types/data-table";
import DeleteCartItem from "./DeleteCartItem/DeleteCartItem";
import "./cart.css";
import DynamicTabs from "../../../components/tab/DynamicTabs";
import NautilusAsset from "../nautilus-asset-page/NautilusAsset";
import { ConnectKitButton } from "connectkit";
import { useAppContext } from "../../../context/app-context";

export default function Cart() {
  const { t } = useTranslation(["components"]);
  const { keycloakProfile, role } = useAppContext();
  const pagination = usePagination();
  const [activeTab, setActiveTab] = useState<number>(1);
  const { onHide, onShow, show } = useDisclosure();
  const {
    onHide: onHideNautilusAsset,
    onShow: onShowNautilusAsset,
    show: showNautilusAsset,
  } = useDisclosure();
  const {
    onHide: onHideAcknowledge,
    onShow: onShowAcknowledge,
    show: showAcknowledge,
  } = useDisclosure();
  const [selectedItem, setSelectedItem] = useState<CartType>();

  useEffect(() => {
    if (+activeTab === 2) {
      onShowNautilusAsset();
    }
  }, [activeTab]);

  const params = useMemo(
    () => ({
      params: {
        page: pagination.page,
        limit: pagination.limit,
        search: keycloakProfile?.email,
      },
    }),
    [pagination.page, pagination.limit, keycloakProfile?.email]
  );
  const { data, refetch, loading } = useData<
    PaginatedApiResponseType<CartType[]>
  >(ApiPaths.cart, {
    options: params,
    onError: () => {
      toast(t("cart.error.fetch_table", { ns: "components" }), {
        type: "error",
      });
    },
  });

  const handleDownload = (
    base64String: string,
    fileName: string = "file.pdf"
  ) => {
    try {
      // Decode base64 string to raw binary data
      const byteCharacters = atob(base64String.split(",")[1]);
      const byteNumbers: number[] = Array.from({
        length: byteCharacters.length,
      });
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.codePointAt(i) ?? 0;
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Create an object URL for the Blob
      const url = URL.createObjectURL(blob);

      // Create a link and trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.append(link);
      link.click();
      link.remove();

      // Clean up the object URL
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download the file. Please try again.");
      console.error("Download error:", error);
    }
  };

  const columnsTraditionalInsurance: ColumnType[] = [
    {
      accessor: "product_name",
      header: t("table_head.product", { ns: "purchaseTraditionalInsurance" }),
      formatDisplay: (row: ProductType) => {
        return (
          <div className="d-flex align-items-center">
            <Link
              to="/"
              className={`d-flex align-items-center text-decoration-none`}
            >
              <img
                src={row?.product_logo}
                alt={row?.product_name}
                className="assets-table-icon"
              />
            </Link>
            {row?.product_name}
          </div>
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "product_owner_name",
      header: t("table_head.product_owner", {
        ns: "purchaseTraditionalInsurance",
      }),
      formatDisplay: (row: ProductType) => {
        return (
          <div className="d-flex align-items-center">
            <AssetsShieldIcon />
            <span className="ms-1">{row?.product_owner_name}</span>
          </div>
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "product_type",
      header: t("table_head.product_type", {
        ns: "purchaseTraditionalInsurance",
      }),
      formatDisplay: (row) => {
        return (
          <div>
            {row?.product_type?.map((item: any) => item?.label).join(", ")}
          </div>
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "cover_wording",
      header: t("table_head.product_details", {
        ns: "purchaseTraditionalInsurance",
      }),
      formatDisplay: (row) => (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleDownload(row?.cover_wording);
          }}
          rel="noreferrer"
        >
          Download <BsArrowUpRight />
        </a>
      ),
      headerAlignment: "center",
      alignment: "center",
    },
    {
      accessor: "product_status",
      header: t("table_head.product_status", {
        ns: "purchaseTraditionalInsurance",
      }),
      formatDisplay: (row) => {
        return row.product_status ? (
          <button
            className={`status-button px-4 py-2 ${row?.product_status?.toLowerCase()}-status`}
          >
            {row?.product_status}
          </button>
        ) : (
          "-"
        );
      },
      headerAlignment: "center",
      alignment: "center",
    },
  ];

  const actionsTraditionalInsurance: ActionType[] = [
    {
      name: "remove",
      action: (row: CartType) => {
        return (
          <CustomButton
            className="btn btn-sm d-flex align-items-center btn-danger"
            onClick={() => {
              setSelectedItem(row);
              onShow();
            }}
          >
            <BsTrash className="me-1" />
            {t("button.remove", { ns: "common" })}
          </CustomButton>
        );
      },
    },
    {
      name: "Purchase",
      action: (row: CartType) => {
        return (
          <CustomButton
            className="btn btn-sm d-flex align-items-center btn-success"
            onClick={() => {
              setSelectedItem(row);
              onShowAcknowledge();
            }}
          >
            <BsCart className="me-1" />
            {t("cart.button.purchase", { ns: "components" })}
          </CustomButton>
        );
      },
    },
  ];

  const TraditionalCovers = () => {
    return (
      <div className="mt-4">
        <div className="my-cart-table mb-3">
          <DataTable
            columns={columnsTraditionalInsurance}
            data={data?.data}
            showSerial={true}
            actions={actionsTraditionalInsurance}
            loading={loading}
          />
        </div>
        <Pagination total={data?.count ?? 1} pagination={pagination} />
      </div>
    );
  };

  const tabs = [
    {
      id: 1,
      title: t("cart.traditional_insurance", { ns: "components" }),
      component: <TraditionalCovers />,
    },
    {
      id: 2,
      title: t("cart.nautilus_asset_covers", { ns: "components" }),
      component: (
        <NautilusAsset
          onHide={onHideNautilusAsset}
          show={showNautilusAsset}
          redirectURL="https://app.nautilusfinance.io/"
        />
      ),
    },
  ];

  const handleTabChange = (selectedTab: number) => {
    setActiveTab(selectedTab);
  };

  return (
    <div className="p-3 pb-5 p-md-4 mb-5">
      <NautilusAsset
        onHide={onHideAcknowledge}
        show={showAcknowledge}
        ClustDoc={true}
        redirectURL="https://app.clustdoc.com/cp/10546639-mdqa8v1FozGHOuCexN3FVpwnv0erVi"
      />
      <DeleteCartItem
        onHide={onHide}
        onSuccess={refetch}
        selectedItem={selectedItem}
        show={show}
      />
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="primary-title ">
          {t("cart.heading", { ns: "components" })}
        </h2>
        <div className="d-flex align-items-center justify-content-between">
          <Link
            to={
              activeTab === 1
                ? "/purchase-traditional-insurance"
                : "/purchase-cover"
            }
            className="primary-button add-more-covers text-decoration-none rounded-pill me-md-4"
          >
            {t("cart.button.add", { ns: "components" })}
          </Link>
          {role === "business-user" ? (
            ""
          ) : (
            <ConnectKitButton.Custom>
              {(props: any) => (
                <div>
                  <button
                    className="d-none d-md-flex primary-button add-more-covers bg-dark"
                    style={{ whiteSpace: "nowrap" }}
                    onClick={props.show}
                  >
                    {props?.isConnecting
                      ? "Connecting..."
                      : t("cart.button.wallet", { ns: "components" })}
                  </button>
                </div>
              )}
            </ConnectKitButton.Custom>
          )}
        </div>
      </div>
      <DynamicTabs
        tabs={tabs}
        currentTab={activeTab}
        currentSelectedTab={handleTabChange}
      />
    </div>
  );
}
